import { usePermissions } from "helpers/usePermission";
import { useFormErrors } from "helpers/useFormErrors";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  ModalPlayerTabs,
  ModalPlayerFinanceTabs,
  ModalPlayerLiveOpsTabs,
  ModalPlayerInfoTabs,
  ModalPlayerRiskTabs,
  ModalPlayerBetsTabs,
  ModalPlayerVerificationTabs,
  ModalTypes,
} from "configs/types";
import { closeModal, openModal } from "store/modals/slice";
import {
  getRestrictions,
  getDepositStatusesList,
  getPlayerNotifsList,
  getNotesList,
  getActivitiesList,
  handleDepositsClear,
  handleCompensationsClear,
  handleConfiscationsClear,
  handleInfoClear,
  getPlayerItem,
  updatePlayer,
  handleNotifsClear,
  getPaymentInfo,
  handleNotesClear,
  handleActivitiesClear,
  handleBetsPaginate,
  handleUpdateFieldsRestrictions,
  updatePlayerRestrictions,
  UserRestrictions,
  updatePlayerRiskProfile,
  PlayerRiskProfile,
  getPlayerRiskProfile,
  RestrictionRow,
  updatePlayerRiskRestriction,
} from "store/players/list/slice";
import {
  getRiskKycDocumentsByUser,
  uploadRiskKycDocument,
} from "store/risk/kycDocuments/slice";
import { playersListLimits } from "configs/limits";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import Button from "components/buttons/Button";
import ListInfoTab from "../tabs/list/ListInfoTab/ListInfoTab";
import ListDepositTab from "../tabs/list/ListFinanceTab/ListDepositTab";
import ListCompensationsTab from "../tabs/list/ListFinanceTab/ListCompensationsTab";
import ListConfiscationsTab from "../tabs/list/ListFinanceTab/ListConfiscationsTab";
import ListNotesTab from "../tabs/list/ListNotesTab/ListNotesTab";
import ListActivitiesTab from "../tabs/list/ListActivitiesTab/ListActivitiesTab";
import ListVerificationTab from "../tabs/list/ListVerificationTab/ListVerificationTab";
import { ProfileDataAction } from "../tabs/list/ListVerificationTab/ProfileDataTab";
import ListBonusesTab from "../tabs/list/ListLiveOpsTab/ListBonusesTab";
import ListLoyaltyTab from "../tabs/list/ListLiveOpsTab/ListLoyaltyTab";
import ListCashbacksTab from "../tabs/list/ListLiveOpsTab/ListCashbacksTab";
import ListPromocodesTab from "../tabs/list/ListLiveOpsTab/ListPromocodesTab";
import BonusModeSwitcher from "../tabs/list/ListLiveOpsTab/BonusModeSwitcher";
import { PlayerBonusListMode } from "types/playerBonuses";
import atIcon from "assets/icons/payments/atIcon.png";
import depIcon from "assets/icons/payments/depIcon.png";
import failedIcon from "assets/icons/payments/failedIcon.png";
import pendingIcon from "assets/icons/payments/pendingIcon.png";
import withIcon from "assets/icons/payments/withIcon.png";
import sucIcon from "assets/icons/payments/sucIcon.png";
import manualDepositIcon from "assets/icons/manualDeposit.svg";
import manualWithdrawalIcon from "assets/icons/manualWithdrawal.svg";
import compensationIcon from "assets/icons/compensation.svg";
import confiscationIcon from "assets/icons/confiscation.svg";
import { Modal } from "components/Modal/Modal";
import styles from "./PlayersListEditModal.module.scss";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import ListWithdrawalsTab from "../tabs/list/ListFinanceTab/ListWithdrawalsTab";
import ListRiskTab from "../tabs/list/ListRiskTab/ListRiskTab";
import ListCasinoTab from "../tabs/list/ListBetsTab/ListCasinoTab";
import ListSportTab from "../tabs/list/ListBetsTab/ListSportTab";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { handleApiError } from "helpers/handleApiError";
import { ResponsibleGamingSaveHandler } from "../tabs/list/ListInfoTab/ResponsibleGamingSection";

const PlayersListEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const restrictionErrors = useFormErrors();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const restrictionsInfo = useAppSelector(
    (store) => store.players.restrictionsInfo,
  );
  const isRestrictionsFetched = useAppSelector(
    (store) => store.players.isRestrictionsFetched,
  );
  const paymentInfo = useAppSelector((store) => store.players.paymentInfo);
  const itemFetched = useAppSelector((store) => store.players.isItemFetched);
  const payload = useAppSelector((store) => store.players.payload);
  const isItemFetching = useAppSelector(
    (store) => store.players.isItemFetching,
  );
  const depositsListFetched = useAppSelector(
    (store) => store.players.isDepositsFetched,
  );
  const depositsStatusesListFetched = useAppSelector(
    (store) => store.players.isStatusesFetched,
  );
  const notesListFetched = useAppSelector(
    (store) => store.players.isNotesFetched,
  );
  const activePageNotes = useAppSelector(
    (store) => store.players.activePageNotes,
  );
  const activePageDeposits = useAppSelector(
    (store) => store.players.activePageDeposits,
  );
  const isNotifsFetched = useAppSelector(
    (store) => store.players.isNotifsFetched,
  );
  const activePageNotifs = useAppSelector(
    (store) => store.players.activePageNotifs,
  );
  const isActivitiesFetched = useAppSelector(
    (store) => store.players.isActivitiesFetched,
  );
  const activePageActivities = useAppSelector(
    (store) => store.players.activePageActivities,
  );
  const riskProfile = useAppSelector((store) => store.players.riskProfile);
  const isRiskProfileFetched = useAppSelector(
    (store) => store.players.isRiskProfileFetched,
  );
  const isRiskRestrictionsFetched = useAppSelector(
    (store) => store.players.isRiskRestrictionsFetched,
  );
  const isUploadingKyc = useAppSelector(
    (store) => store.riskKycDocuments.isUploading,
  );
  const isKycDocumentsFetched = useAppSelector(
    (store) => store.riskKycDocuments.isListFetched,
  );
  const [currentLimit, setCurrentLimit] = useState<number>(playersListLimits[0]);
  const [riskRestrictionRows, setRiskRestrictionRows] = useState<
    RestrictionRow[]
  >([]);
  const [initialRiskRestrictionRows, setInitialRiskRestrictionRows] = useState<
    RestrictionRow[]
  >([]);
  const [verificationFiles, setVerificationFiles] = useState<
    Record<string, File | null>
  >({});
  const [profileDataAction, setProfileDataAction] =
    useState<ProfileDataAction | null>(null);
  const responsibleGamingSaveRef = useRef<ResponsibleGamingSaveHandler | null>(
    null,
  );

  const [tabs] = useState<ModalPlayerTabs[]>([
    ModalPlayerTabs.infoUser,
    ModalPlayerTabs.bets,
    ModalPlayerTabs.finance,
    ModalPlayerTabs.liveOps,
    ModalPlayerTabs.verification,
    ModalPlayerTabs.risk,
    ModalPlayerTabs.notes,
    ModalPlayerTabs.activities,
  ]);

  const tabFromUrl = searchParams.get("tab");

  const [activeTab, setActiveTab] = useState<ModalPlayerTabs>(
    (tabFromUrl as ModalPlayerTabs) || ModalPlayerTabs.infoUser,
  );

  useEffect(() => {
    if (tabFromUrl && tabs.includes(tabFromUrl as ModalPlayerTabs)) {
      setActiveTab(tabFromUrl as ModalPlayerTabs);
    }
  }, [tabFromUrl, tabs]);

  const [tabsFinance] = useState<ModalPlayerFinanceTabs[]>([
    ModalPlayerFinanceTabs.deposits,
    ModalPlayerFinanceTabs.withdrawal,
    ModalPlayerFinanceTabs.compensations,
    ModalPlayerFinanceTabs.confiscations,
  ]);
  const [activeTabFinance, setActiveTabFinance] =
    useState<ModalPlayerFinanceTabs>(ModalPlayerFinanceTabs.deposits);
  const [tabsLiveOps] = useState<ModalPlayerLiveOpsTabs[]>([
    ModalPlayerLiveOpsTabs.bonuses,
    ModalPlayerLiveOpsTabs.loyalty,
    ModalPlayerLiveOpsTabs.tournaments,
    ModalPlayerLiveOpsTabs.cashBacks,
    ModalPlayerLiveOpsTabs.promoCodes,
  ]);
  const [activeTabLiveOps, setActiveTabLiveOps] =
    useState<ModalPlayerLiveOpsTabs>(ModalPlayerLiveOpsTabs.bonuses);
  const [bonusesMode, setBonusesMode] =
    useState<PlayerBonusListMode>("attached");
  const [bonusesMeta, setBonusesMeta] = useState({
    totalItems: 0,
    isLoading: false,
  });
  const handleBonusesMetaChange = useCallback(
    (meta: { totalItems: number; isLoading: boolean }) => {
      setBonusesMeta(meta);
    },
    [],
  );
  const [tabsInfo] = useState<ModalPlayerInfoTabs[]>([
    ModalPlayerInfoTabs.main,
    ModalPlayerInfoTabs.wagering,
    ModalPlayerInfoTabs.restrictions,
    ModalPlayerInfoTabs.notifications,
  ]);
  const [activeTabInfo, setActiveTabInfo] = useState<ModalPlayerInfoTabs>(
    ModalPlayerInfoTabs.main,
  );
  const [activeRiskTab, setActiveRiskTab] = useState<ModalPlayerRiskTabs>(
    ModalPlayerRiskTabs.profile,
  );
  const [tabsBets] = useState<ModalPlayerBetsTabs[]>([
    ModalPlayerBetsTabs.casino,
    ModalPlayerBetsTabs.sport,
  ]);

  const [activeTabBets, setActiveTabBets] = useState<ModalPlayerBetsTabs>(
    ModalPlayerBetsTabs.casino,
  );
  const [showRestrictionErrors, setShowRestrictionErrors] = useState(false);

  const permissions = usePermissionHelper();
  const canRead = permissions?.read ?? false;
  const canUpdate = permissions?.update ?? false;
  const canCreate = permissions?.create ?? false;
  const withdrawalPermissions = usePermissions("payments_transactions_withdrawals");
  const canManualWithdrawal = withdrawalPermissions?.includes("update") ?? false;

  const isFooterLoading =
    (activeTab === ModalPlayerTabs.infoUser &&
      activeTabInfo === ModalPlayerInfoTabs.restrictions &&
      !isRestrictionsFetched) ||
    (activeTab === ModalPlayerTabs.verification &&
      (searchParams.get("verificationTab") ===
      ModalPlayerVerificationTabs.profileData
        ? !profileDataAction || profileDataAction.isLoading
        : !isKycDocumentsFetched)) ||
    (activeTab === ModalPlayerTabs.risk &&
      activeRiskTab === ModalPlayerRiskTabs.profile &&
      !isRiskProfileFetched) ||
    (activeTab === ModalPlayerTabs.risk &&
      activeRiskTab === ModalPlayerRiskTabs.restrictions &&
      !isRiskRestrictionsFetched);

  const visibleTabs = canRead
    ? tabs
    : tabs.filter((tab) => tab !== ModalPlayerTabs.finance);

  useEffect(() => {
    const entityId = searchParams.get("entityId");
    if (
      entityId !== null &&
      activeTab === ModalPlayerTabs.infoUser &&
      activeTabInfo === ModalPlayerInfoTabs.restrictions
    ) {
      dispatch(getRestrictions(entityId));
    }
  }, [activeTab, activeTabInfo, dispatch, searchParams]);

  useEffect(
    () => () => {
      dispatch(handleUpdateFieldsRestrictions(undefined));
    },
    [dispatch],
  );

  useEffect(() => {
    const entityId = searchParams.get("entityId");

    if (canRead && entityId !== null && activeTab === ModalPlayerTabs.finance) {
      dispatch(getPaymentInfo(entityId));
    }
  }, [activeTab, canRead, dispatch, searchParams]);

  useEffect(() => {
    if (searchParams.get("entityId") !== null) {
      if (activeTab === ModalPlayerTabs.activities && !isActivitiesFetched) {
        dispatch(
          getActivitiesList({
            limit: currentLimit,
            offset: activePageActivities - 1,
            id: parseInt(searchParams.get("entityId") as string, 10),
          }),
        );
      }
      if (
        activeTab === ModalPlayerTabs.infoUser &&
        activeTabInfo === ModalPlayerInfoTabs.notifications &&
        !isNotifsFetched
      ) {
        dispatch(
          getPlayerNotifsList({
            limit: currentLimit,
            offset: activePageNotifs - 1,
            id: parseInt(searchParams.get("entityId") as string, 10),
          }),
        );
      }
      if (!itemFetched) {
        dispatch(
          getPlayerItem(parseInt(searchParams.get("entityId") as string, 10)),
        );
      }
      if (activeTab === ModalPlayerTabs.notes && !notesListFetched) {
        dispatch(
          getNotesList({
            limit: currentLimit,
            offset: activePageNotes - 1,
            id: parseInt(searchParams.get("entityId") as string, 10),
          }),
        );
      }
      if (
        canRead &&
        activeTab === ModalPlayerTabs.finance &&
        !depositsStatusesListFetched
      ) {
        dispatch(getDepositStatusesList());
      }
    }
  }, [
    searchParams,
    activeTab,
    currentLimit,
    itemFetched,
    isNotifsFetched,
    activeTabInfo,
    activePageNotifs,
    activePageDeposits,
    depositsStatusesListFetched,
    depositsListFetched,
    notesListFetched,
    activePageNotes,
    activePageActivities,
    isActivitiesFetched,
    canRead,
  ]);

  const isLoaded = () => {
    if (!itemFetched) {
      return false;
    }

    return true;
  };

  const handleTabClick = (e: ModalPlayerTabs) => {
    setActiveTab(e);
  };
  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleDepositsClear());
    dispatch(handleCompensationsClear());
    dispatch(handleConfiscationsClear());
    dispatch(handleInfoClear());
    dispatch(handleNotifsClear());
    dispatch(handleNotesClear());
    dispatch(handleActivitiesClear());
  };

  const renderBetsTab = () => {
    if (activeTabBets === ModalPlayerBetsTabs.casino) {
      return <ListCasinoTab />;
    }

    if (activeTabBets === ModalPlayerBetsTabs.sport) {
      return <ListSportTab />;
    }
  };

  const renderFinanceTab = () => {
    if (!canRead) {
      return null;
    }

    if (activeTabFinance === ModalPlayerFinanceTabs.deposits) {
      return <ListDepositTab canUpdate={canUpdate} />;
    }
    if (activeTabFinance === ModalPlayerFinanceTabs.withdrawal) {
      return <ListWithdrawalsTab canUpdate={canUpdate} />;
    }
    if (activeTabFinance === ModalPlayerFinanceTabs.compensations) {
      return <ListCompensationsTab />;
    }
    if (activeTabFinance === ModalPlayerFinanceTabs.confiscations) {
      return <ListConfiscationsTab />;
    }
  };

  const handleOpenManualWithdrawal = () => {
    if (!canManualWithdrawal) return;
    navigate(`${location.pathname}?activeModal=${ModalTypes.createWithdrawal}&entityId=${searchParams.get("entityId")}`);
    dispatch(openModal({ activeModal: ModalTypes.createWithdrawal }));
  };

  const handleOpenManualDeposit = () => {
    if (!canCreate) return;

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.createDeposit}&entityId=${searchParams.get("entityId")}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.createDeposit }));
  };

  const handleOpenCompensation = () => {
    if (!canCreate) return;

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.createCompensation}&entityId=${searchParams.get("entityId")}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.createCompensation }));
  };

  const handleOpenConfiscation = () => {
    if (!canCreate) return;

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.createConfiscation}&entityId=${searchParams.get("entityId")}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.createConfiscation }));
  };

  const renderLiveOpsTab = () => {
    if (activeTabLiveOps === ModalPlayerLiveOpsTabs.bonuses) {
      return (
        <ListBonusesTab
          mode={bonusesMode}
          onMetaChange={handleBonusesMetaChange}
          canCreate={canCreate}
          canUpdate={canUpdate}
        />
      );
    }
    if (activeTabLiveOps === ModalPlayerLiveOpsTabs.loyalty) {
      return <ListLoyaltyTab />;
    }
    if (activeTabLiveOps === ModalPlayerLiveOpsTabs.cashBacks) {
      return <ListCashbacksTab />;
    }
    if (activeTabLiveOps === ModalPlayerLiveOpsTabs.promoCodes) {
      return <ListPromocodesTab />;
    }
    return <div className="player-content-wrapper"></div>;
  };

  const renderTab = () => {
    if (activeTab === ModalPlayerTabs.verification) {
      return (
        <ListVerificationTab
          files={verificationFiles}
          setFiles={setVerificationFiles}
          onProfileDataActionChange={setProfileDataAction}
        />
      );
    }
    if (activeTab === ModalPlayerTabs.infoUser) {
      return (
        <ListInfoTab
          fieldErrors={restrictionErrors.errors}
          clearFieldError={restrictionErrors.clearFieldError}
          tabs={tabsInfo}
          activeTab={activeTabInfo}
          onResponsibleGamingSaveHandlerChange={(handler) => {
            responsibleGamingSaveRef.current = handler;
          }}
          notificationsLimit={currentLimit}
          onNotificationsLimitChange={setCurrentLimit}
          setActiveTab={(e) => {
            setActiveTabInfo(e as ModalPlayerInfoTabs);
          }}
        />
      );
    }
    if (activeTab === ModalPlayerTabs.bets) {
      return (
        <div className="player-bets-wrap">
          <div className="game-modal-tabs segment-tabs small">
            {tabsBets.map((el) => (
              <div
                className={`tab-item ${activeTabBets === el ? "active" : ""}`}
                key={el}
                onClick={() => {
                  dispatch(handleBetsPaginate(1));
                  setActiveTabBets(el);
                }}
              >
                {t(el)}
              </div>
            ))}
          </div>

          {renderBetsTab()}
        </div>
      );
    }
    if (activeTab === ModalPlayerTabs.notes) {
      return <ListNotesTab limit={currentLimit} onLimitChange={setCurrentLimit} />;
    }
    if (activeTab === ModalPlayerTabs.activities) {
      return <ListActivitiesTab limit={currentLimit} onLimitChange={setCurrentLimit} />;
    }
    if (activeTab === ModalPlayerTabs.liveOps) {
      return (
        <div className="player-liveops-wrap">
          <div className="game-modal-tabs segment-tabs small">
            {tabsLiveOps.map((el) => (
              <div
                className={`tab-item ${activeTabLiveOps === el ? "active" : ""}`}
                key={el}
                onClick={() => {
                  setActiveTabLiveOps(el);
                }}
              >
                {t(el)}
              </div>
            ))}
          </div>
          {renderLiveOpsTab()}
        </div>
      );
    }
    if (activeTab === ModalPlayerTabs.finance && canRead) {
      return (
        <div className="player-finance-wrap">
          <div className="payment-info-row">
            <div className="paymeny-info-item">
              <div className="title">{t("sumCounts")}</div>
              <div className="value">
                {paymentInfo?.depositSum}/{paymentInfo?.depositCount}
              </div>
              <img src={depIcon} alt="" />
            </div>

            <div className="paymeny-info-item">
              <div className="title">{t("sumCounts")}</div>
              <div className="value">
                {paymentInfo?.withdrawalSum}/{paymentInfo?.withdrawalCount}
              </div>
              <img src={withIcon} alt="" />
            </div>

            <div className="paymeny-info-item">
              <div className="title">{t("failed")}</div>
              <div className="value">
                {paymentInfo?.depositFailed}/{paymentInfo?.withdrawalFailed}
              </div>
              <img src={failedIcon} alt="" />
            </div>

            <div className="paymeny-info-item">
              <div className="title">{t("attempt")}</div>
              <div className="value">
                {paymentInfo?.depositAttempt}/{paymentInfo?.withdrawalAttempt}
              </div>
              <img src={atIcon} alt="" />
            </div>

            <div className="paymeny-info-item">
              <div className="title">{t("pending")}</div>
              <div className="value">
                {paymentInfo?.depositPending}/{paymentInfo?.withdrawalPending}
              </div>
              <img src={pendingIcon} alt="" />
            </div>

            <div className="paymeny-info-item">
              <div className="title">{t("succeed")}</div>
              <div className="value">
                {paymentInfo?.depositSucceed}/{paymentInfo?.withdrawalSucceed}
              </div>
              <img src={sucIcon} alt="" />
            </div>
          </div>
          <div className="player-finance-toolbar">
            <div className="game-modal-tabs segment-tabs small">
              {tabsFinance.map((el) => (
                <div
                  className={`tab-item ${activeTabFinance === el ? "active" : ""}`}
                  key={el}
                  onClick={() => {
                    setActiveTabFinance(el);
                  }}
                >
                  {t(el)}
                </div>
              ))}
            </div>

            {(canCreate || canManualWithdrawal) && (
              <div className="player-finance-actions">
                {canCreate && (
                  <button
                    className="finance-action finance-action--deposit"
                    type="button"
                    onClick={handleOpenManualDeposit}
                  >
                    <img
                      className="finance-action__icon"
                      src={manualDepositIcon}
                      alt="manual deposit"
                    />
                    {t("manualDeposit")}
                  </button>
                )}
                {canManualWithdrawal && (
                  <button
                    className="finance-action finance-action--withdrawal"
                    type="button"
                    onClick={handleOpenManualWithdrawal}
                  >
                    <img
                      className="finance-action__icon"
                      src={manualWithdrawalIcon}
                      alt="manual withdrawal"
                    />
                    {t("withdrawal")}
                  </button>
                )}
                {canCreate && (
                  <>
                    <button
                      className="finance-action finance-action--compensation"
                      type="button"
                      onClick={handleOpenCompensation}
                    >
                      <img
                        className="finance-action__icon"
                        src={compensationIcon}
                        alt="compensation"
                      />
                      {t("compensation")}
                    </button>
                    <button
                      className="finance-action finance-action--confiscation"
                      type="button"
                      onClick={handleOpenConfiscation}
                    >
                      <img
                        className="finance-action__icon"
                        src={confiscationIcon}
                        alt="confiscation"
                      />
                      {t("confiscation")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          {renderFinanceTab()}
        </div>
      );
    }
    if (activeTab === ModalPlayerTabs.risk) {
      return (
        <ListRiskTab
          rows={riskRestrictionRows}
          setRows={setRiskRestrictionRows}
          setInitialRows={setInitialRiskRestrictionRows}
          showRestrictionErrors={showRestrictionErrors}
          activeTab={activeRiskTab}
          setActiveTab={setActiveRiskTab}
        />
      );
    }
    return <div className="game-form-wrapper"></div>;
  };

  const handleApply = async () => {
    const canApply =
      (activeTab === ModalPlayerTabs.infoUser &&
        (activeTabInfo === ModalPlayerInfoTabs.main ||
          activeTabInfo === ModalPlayerInfoTabs.restrictions) &&
        canUpdate) ||
      (activeTab === ModalPlayerTabs.verification && canCreate) ||
      (activeTab === ModalPlayerTabs.risk &&
        activeRiskTab !== ModalPlayerRiskTabs.calculated &&
        canUpdate);

    if (!canApply) {
      return;
    }

    if (
      activeTab === ModalPlayerTabs.infoUser &&
      activeTabInfo === ModalPlayerInfoTabs.main &&
      payload
    ) {
      const result = await dispatch(updatePlayer(payload));
      if (result.payload === true) {
        if (payload.id) {
          await dispatch(getPlayerItem(payload.id));
        }
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        handleApiError({ error: result.payload, t, dispatch });
      }
    }
    if (
      activeTab === ModalPlayerTabs.infoUser &&
      activeTabInfo === ModalPlayerInfoTabs.restrictions
    ) {
      const id = searchParams.get("entityId") ?? "";
      try {
        const responsibleGamingSaved =
          (await responsibleGamingSaveRef.current?.()) ?? true;
        if (!responsibleGamingSaved) return;

        restrictionErrors.resetErrors();
        await dispatch(
          updatePlayerRestrictions({
            id,
            ...restrictionsInfo,
          } as UserRestrictions & { id: string }),
        ).unwrap();
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch, formErrors: restrictionErrors });
      }
    }
    if (activeTab === ModalPlayerTabs.verification) {
      const id = searchParams.get("entityId");

      if (!id) {
        return;
      }

      try {
        const uploads = Object.entries(verificationFiles);
        if (!uploads.length) {
          return;
        }
        await Promise.all(
          uploads.map(([side, file]) => {
            if (!file) {
              return Promise.resolve();
            }

            return dispatch(
              uploadRiskKycDocument({
                userId: Number(id),
                documentType: searchParams.get("verificationTab") || "passport",
                documentSide: side,
                file,
              }),
            ).unwrap();
          }),
        );

        dispatch(getRiskKycDocumentsByUser(Number(id)));

        setVerificationFiles({});

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch });
      }
    }
    if (
      activeTab === ModalPlayerTabs.risk &&
      activeRiskTab === ModalPlayerRiskTabs.profile
    ) {
      const id = searchParams.get("entityId") ?? "";

      try {
        if (riskProfile) {
          await dispatch(
            updatePlayerRiskProfile({
              id,
              ...riskProfile,
            } as PlayerRiskProfile & { id: string }),
          ).unwrap();

          await dispatch(getPlayerRiskProfile(id));
        }

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch });
      }
    }
    if (
      activeTab === ModalPlayerTabs.risk &&
      activeRiskTab === ModalPlayerRiskTabs.restrictions
    ) {
      const hasEmptyReasons = riskRestrictionRows.some(
        (row) => row.isBlocked && !row.reason.trim(),
      );

      if (hasEmptyReasons) {
        setShowRestrictionErrors(true);

        dispatch(
          showNotification({
            title: t("warning"),
            message: t("fillRestrictionReason"),
            type: NotificationTypes.warning,
          }),
        );

        return;
      }

      setShowRestrictionErrors(false);

      const id = searchParams.get("entityId") ?? "";

      try {
        const changedRows = riskRestrictionRows.filter((row) => {
          const initialRow = initialRiskRestrictionRows.find(
            (item) => item.restrictionType === row.restrictionType,
          );

          return (
            initialRow?.isBlocked !== row.isBlocked ||
            initialRow?.reason !== row.reason
          );
        });

        await dispatch(
          updatePlayerRiskRestriction({
            id,
            items: changedRows.map((row) => ({
              restrictionType: row.restrictionType,
              isBlocked: row.isBlocked ? 1 : 0,
              reason: row.reason,
            })),
          }),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        dispatch(
          getPlayerItem(parseInt(searchParams.get("entityId") as string, 10)),
        );
      } catch (error) {
        handleApiError({ error, t, dispatch });
      }
    }

    if (
      activeTab === ModalPlayerTabs.risk &&
      activeRiskTab === ModalPlayerRiskTabs.calculated
    ) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("playerEditing")}</Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={visibleTabs}
            activeTab={activeTab}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={`${styles.playerEditContentWrapper} ${
              activeTab === ModalPlayerTabs.finance
                ? styles.playerFinanceContentWrapper
                : ""
            } ${
              activeTab === ModalPlayerTabs.bets
                ? styles.playerBetsContentWrapper
                : ""
            } ${
              activeTab === ModalPlayerTabs.infoUser &&
              (activeTabInfo === ModalPlayerInfoTabs.wagering ||
                activeTabInfo === ModalPlayerInfoTabs.notifications)
                ? styles.playerWageringContentWrapper
                : ""
            } ${
              activeTab === ModalPlayerTabs.activities
                ? styles.playerActivitiesContentWrapper
                : ""
            } ${
              activeTab === ModalPlayerTabs.notes
                ? styles.playerNotesContentWrapper
                : ""
            } ${
              activeTab === ModalPlayerTabs.liveOps &&
              (activeTabLiveOps === ModalPlayerLiveOpsTabs.loyalty ||
                activeTabLiveOps === ModalPlayerLiveOpsTabs.cashBacks ||
                activeTabLiveOps === ModalPlayerLiveOpsTabs.promoCodes)
                ? styles.playerLoyaltyContentWrapper
                : ""
            } ${
              activeTab === ModalPlayerTabs.liveOps &&
              activeTabLiveOps === ModalPlayerLiveOpsTabs.bonuses
                ? styles.playerBonusesContentWrapper
                : ""
            }`}
          >
            {renderTab()}
          </Modal.Content>

          {activeTab !== ModalPlayerTabs.bets &&
            activeTab !== ModalPlayerTabs.activities &&
            activeTab !== ModalPlayerTabs.notes &&
            activeTab !== ModalPlayerTabs.finance &&
            !(
              activeTab === ModalPlayerTabs.liveOps &&
              (activeTabLiveOps === ModalPlayerLiveOpsTabs.loyalty ||
                activeTabLiveOps === ModalPlayerLiveOpsTabs.cashBacks ||
                activeTabLiveOps === ModalPlayerLiveOpsTabs.promoCodes)
            ) &&
            !(
              activeTab === ModalPlayerTabs.infoUser &&
              (activeTabInfo === ModalPlayerInfoTabs.wagering ||
                activeTabInfo === ModalPlayerInfoTabs.notifications)
            ) && (
              <Modal.Footer className={styles.playerModalFooter}>
                {activeTab === ModalPlayerTabs.liveOps &&
                  activeTabLiveOps === ModalPlayerLiveOpsTabs.bonuses && (
                    <BonusModeSwitcher
                      mode={bonusesMode}
                      totalItems={bonusesMeta.totalItems}
                      isLoading={bonusesMeta.isLoading}
                      onChange={setBonusesMode}
                    />
                  )}
                {((activeTab === ModalPlayerTabs.infoUser &&
                  (activeTabInfo === ModalPlayerInfoTabs.main ||
                    activeTabInfo === ModalPlayerInfoTabs.restrictions) &&
                  canUpdate) ||
                  (activeTab === ModalPlayerTabs.verification && canCreate) ||
                  (activeTab === ModalPlayerTabs.risk &&
                    activeRiskTab !== ModalPlayerRiskTabs.calculated &&
                    canUpdate)) &&
                  (isFooterLoading ? (
                    <div className={styles.footerSkeleton}>
                      <ModalFooterSkeleton />
                    </div>
                  ) : (
                    <div className={styles.footerActions}>
                      <Button
                        isSecondary
                        txt={t("cancel")}
                        handleClick={handleCloseClick}
                      />
                      <Button
                        isPrimary={
                          !(
                            activeTab === ModalPlayerTabs.verification &&
                            searchParams.get("verificationTab") ===
                              ModalPlayerVerificationTabs.profileData
                          )
                        }
                        isGreen={
                          activeTab === ModalPlayerTabs.verification &&
                          searchParams.get("verificationTab") ===
                            ModalPlayerVerificationTabs.profileData
                        }
                        handleClick={
                          activeTab === ModalPlayerTabs.verification &&
                          searchParams.get("verificationTab") ===
                            ModalPlayerVerificationTabs.profileData
                            ? profileDataAction?.approve
                            : handleApply
                        }
                        txt={
                          activeTab === ModalPlayerTabs.verification &&
                          searchParams.get("verificationTab") ===
                            ModalPlayerVerificationTabs.profileData
                            ? profileDataAction?.status === "approved"
                              ? "Approved"
                              : "Approve"
                            : t("save")
                        }
                        disabled={
                          activeTab === ModalPlayerTabs.verification &&
                          searchParams.get("verificationTab") ===
                            ModalPlayerVerificationTabs.profileData
                            ? !profileDataAction?.configured ||
                              !profileDataAction.canApprove
                            : false
                        }
                        isLoad={
                          activeTab === ModalPlayerTabs.verification &&
                          searchParams.get("verificationTab") ===
                            ModalPlayerVerificationTabs.profileData
                            ? profileDataAction?.isApproving
                            : isItemFetching || isUploadingKyc
                        }
                      />
                    </div>
                  ))}
              </Modal.Footer>
            )}
        </>
      )}
    </Modal>
  );
};

export default PlayersListEditModal;
